import React from 'react';

/**
 * SectionHeader — V3.5 editorial primitive.
 *
 * Signature block used above every major Homepage section (AI Chat,
 * Hidden Gem, Today's Deals, How It Works). Ties the whole page to the
 * same editorial rhythm the hero establishes with its "◆" signature mark.
 *
 * Anatomy (top → bottom):
 *   • Uppercase kicker with hairline dividers on either side
 *   • Amber diamond ◆ signature (matches the hero)
 *   • H2 heading in a large, elegant Inter extra-bold
 *   • Optional muted subtitle
 *
 * The block scales its rhythm on mobile via `text-2xl sm:text-3xl` and
 * shrinks the hairlines to avoid feeling cramped. Zero decoration on
 * disabled JS environments — pure CSS.
 */
export default function SectionHeader({
  kicker,
  title,
  subtitle,
  align = 'center',
  testId,
}) {
  const alignCls = align === 'left' ? 'text-left items-start' : 'text-center items-center';
  const dividerJustify = align === 'left' ? 'justify-start' : 'justify-center';
  return (
    <div
      className={`flex flex-col ${alignCls} gap-2 mb-6 sm:mb-8`}
      data-testid={testId || 'section-header'}
    >
      {kicker && (
        <div className={`flex items-center gap-2 ${dividerJustify}`} data-testid={`${testId || 'section-header'}-kicker`}>
          {align === 'center' && <span aria-hidden className="w-6 sm:w-8 h-px bg-gradient-to-r from-transparent to-gray-300" />}
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-bold text-gray-500">
            {kicker}
          </span>
          <span aria-hidden className="w-6 sm:w-8 h-px bg-gradient-to-l from-transparent to-gray-300" />
        </div>
      )}
      <div className={`flex items-center gap-2 ${dividerJustify}`} aria-hidden>
        <span className="text-brand-amber-500 text-xs">◆</span>
      </div>
      <h2 className="font-extrabold text-2xl sm:text-3xl lg:text-4xl text-gray-900 tracking-tight leading-tight max-w-2xl">
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm sm:text-base text-gray-600 max-w-xl leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}
